<template>
  <div class="tw-container px-4 py-6 has-action-bar">

    <!-- Header -->
    <div class="mb-6">
      <h2 class="text-2xl font-bold text-gray-900">{{ $t('community_settings_title') }}</h2>
      <p class="text-sm text-gray-500 mt-1">{{ $t('community_settings_subtitle') }}</p>
    </div>

    <!-- Loading -->
    <div v-if="fetchingContent" class="tw-flex items-center justify-center py-16">
      <Loading/>
    </div>

    <div v-else-if="communityData" class="tw-flex flex-col gap-5">

      <!-- General -->
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <h3 class="text-base font-semibold text-gray-900 m-0 mb-4">{{ $t('community_settings_section_general') }}</h3>
        <div class="space-y-4">
          <div style="max-width: 480px;">
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              {{ $t('community_settings_field_name') }} <span class="text-red-500">*</span>
            </label>
            <DsInput
              v-model="communityData.name"
              is-simplified
              :placeholder="$t('community_settings_field_name')"
              :class="{ 'border-red-400': errors && errors.name }"
            />
            <p v-if="errors && errors.name" class="text-xs text-red-500 mt-1">{{ errors.name[0] }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              {{ $t('community_settings_field_description') }} <span class="text-red-500">*</span>
            </label>
            <DsTextarea
              v-model="communityData.description"
              :is-simplified="true"
              :default-text="$t('community_settings_field_description_placeholder')"
              class="cs-textarea"
              :class="{ 'border-red-400': errors && errors.description }"
            />
            <p v-if="errors && errors.description" class="text-xs text-red-500 mt-1">{{ errors.description[0] }}</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">{{ $t('community_settings_field_about') }}</label>
            <RichTextArea
              v-model="communityData.about"
              :is-simplified="true"
              :default-text="$t('community_settings_field_about_placeholder')"
              class="cs-textarea"
            />
            <p v-if="errors && errors.about" class="text-xs text-red-500 mt-1">{{ errors.about[0] }}</p>
          </div>
        </div>
      </div>

      <!-- Access -->
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <h3 class="text-base font-semibold text-gray-900 m-0 mb-4">{{ $t('community_settings_section_access') }}</h3>
        <div class="space-y-4" style="max-width: 480px;">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('community_settings_field_visibility') }}</label>
            <span class="block text-xs text-gray-400 mb-1.5">{{ $t('community_settings_field_visibility_hint') }}</span>
            <Dropdown v-model="communityData.visibility" :options="visibilityOptions" is-simplified/>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('community_settings_field_accessibility') }}</label>
            <span class="block text-xs text-gray-400 mb-1.5">{{ $t('community_settings_field_accessibility_hint') }}</span>
            <Dropdown v-model="communityData.accessLevel" :options="accessLevelOptions" is-simplified/>
          </div>
        </div>
      </div>

      <!-- Features -->
      <div>
        <h3 class="text-base font-semibold text-gray-900 mb-3">{{ $t('community_settings_section_features') }}</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">

          <!-- Companies -->
          <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-5 tw-flex flex-col gap-3">
            <div class="tw-flex items-center justify-between">
              <div class="rounded-lg bg-purple-50 p-2 tw-flex items-center justify-center">
                <Icon name="building" size="20" fill="#a855f7"/>
              </div>
              <ToggleButton
                :enabled="communityData.enableActors"
                @update:modelValue="communityData.enableActors = $event"
              />
            </div>
            <div>
              <span class="block text-sm font-semibold text-gray-900">{{ actorsLabel }}</span>
              <span class="block text-xs text-gray-400 mt-1">{{ $t('community_settings_feature_actors_hint') }}</span>
            </div>
          </div>

          <!-- Announcements & Events -->
          <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-5 tw-flex flex-col gap-3" :class="{ 'opacity-50': !areMessageBoardsEnabled }">
            <div class="tw-flex items-center justify-between">
              <div class="rounded-lg bg-blue-50 p-2 tw-flex items-center justify-center">
                <Icon name="bullhorn" size="20" fill="#3b82f6"/>
              </div>
              <div :class="{ 'pointer-events-none': !areMessageBoardsEnabled }">
                <ToggleButton
                  :enabled="communityData.enableAnnouncementsAndEvents"
                  @update:modelValue="communityData.enableAnnouncementsAndEvents = $event"
                />
              </div>
            </div>
            <div>
              <span class="block text-sm font-semibold text-gray-900">{{ announcementsAndEventsLabel }}</span>
              <span class="block text-xs text-gray-400 mt-1">{{ $t('community_settings_feature_announcements_hint') }}</span>
            </div>
          </div>

          <!-- Challenges -->
          <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-5 tw-flex flex-col gap-3" :class="{ 'opacity-50': !areChallengesEnabled }">
            <div class="tw-flex items-center justify-between">
              <div class="rounded-lg bg-orange-50 p-2 tw-flex items-center justify-center">
                <Icon name="comment" size="20" fill="#f97316"/>
              </div>
              <div :class="{ 'pointer-events-none': !areChallengesEnabled }">
                <ToggleButton
                  :enabled="communityData.enableChallenges"
                  @update:modelValue="communityData.enableChallenges = $event"
                />
              </div>
            </div>
            <div>
              <span class="block text-sm font-semibold text-gray-900">{{ challengesLabel }}</span>
              <span class="block text-xs text-gray-400 mt-1">{{ $t('community_settings_feature_challenges_hint') }}</span>
            </div>
          </div>

          <!-- Knowledge Base -->
          <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-5 tw-flex flex-col gap-3">
            <div class="tw-flex items-center justify-between">
              <div class="rounded-lg bg-green-50 p-2 tw-flex items-center justify-center">
                <Icon name="book" size="20" fill="#22c55e"/>
              </div>
              <ToggleButton
                :enabled="communityData.enableKnowledgeBase"
                @update:modelValue="communityData.enableKnowledgeBase = $event"
              />
            </div>
            <div>
              <span class="block text-sm font-semibold text-gray-900">{{ knowledgeBaseLabel }}</span>
              <span class="block text-xs text-gray-400 mt-1">{{ $t('community_settings_feature_knowledge_base_hint') }}</span>
            </div>
          </div>

        </div>
      </div>

      <!-- Appearance -->
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <h3 class="text-base font-semibold text-gray-900 m-0 mb-4">{{ $t('community_settings_section_appearance') }}</h3>
        <div class="grid md:grid-cols-2 grid-cols-1 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">{{ $t('community_settings_field_image') }}</label>
            <ImageInput
              width="512"
              height="288"
              :allow-remove="true"
              :placeholder="$t('community_image_placeholder')"
              :is-simplified="true"
              v-model="communityData.image"
              @input="communityData.image_name = $event"
              @remove="communityData.image = ''"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">{{ $t('community_settings_field_color') }}</label>
            <div class="relative group cursor-pointer" style="max-width: 320px;">
              <div
                class="relative w-full rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 border-2 border-gray-200 group-hover:border-gray-400 group-hover:scale-[1.02]">
                <div class="w-full h-12 relative transition-all duration-300" :style="{ backgroundColor: communityData.color }">
                  <div class="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-black/15"/>
                  <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div
                      class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"/>
                  </div>
                  <div class="absolute top-2 left-2">
                    <div class="bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-md">
                      <span class="text-xs font-bold font-mono text-gray-900 tracking-wide">{{ (communityData.color || '').toUpperCase() }}</span>
                    </div>
                  </div>
                  <div class="absolute bottom-2 right-2 transform transition-all duration-300 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0">
                    <div class="bg-white/90 px-3 py-1.5 rounded-lg shadow-lg tw-flex items-center gap-1.5">
                      <Icon name="paint-brush" size="14" fill="#374151"/>
                      <span class="text-xs font-semibold text-gray-700">{{ $t('community_settings_color_change') }}</span>
                    </div>
                  </div>
                </div>
                <input type="color" v-model="communityData.color" class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"/>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- General error banner -->
    <div v-if="errors && errors.message" class="mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg tw-flex items-center gap-2 text-sm text-red-700">
      <Icon name="warning" size="16" fill="#b91c1c"/>
      {{ errors.message }}
    </div>

    <ActionBar :editing="isEditing">
      <DsButton variant="rounded" :label="saving ? $t('community_settings_saving') : $t('community_settings_save')" :disabled="saving" @click="saveCommunity"/>
      <span class="action-bar__message" v-if="hasFieldErrors">{{ $t('community_settings_error_fields') }}</span>
      <span class="action-bar__message" v-else-if="saved && !saving">{{ $t('community_settings_saved') }}</span>
    </ActionBar>

  </div>
</template>

<script lang="ts">
  import { mix } from 'polished'
  import { ManageCommunities } from '@/api/communities'
  import { ACTION_TYPES as COMMUNITY_ACTION_TYPES, MUTATION_TYPES as COMMUNITY_MUTATION_TYPES } from '@/store/modules/communities'
  import { defineComponent } from 'vue'
  import isEqual from 'lodash/isEqual'
  import TranslationsMixin from '../../util/TranslationsMixin'
  import CommunityMixin from '../../util/CommunityMixin'
  import Dropdown from '../../components/Dropdown/Dropdown.vue'
  import ImageInput from '../../components/Form/ImageInput.vue'
  import DsTextarea from '../../components/Form/DsTextarea.vue'
  import Loading from '../../components/Dashboard/ConceptMap/Loading.vue'
  import ToggleButton from '../../components/Form/ToggleButton.vue'
  import ActionBar from '../../components/Form/ActionBar.vue'
  import RichTextArea from '@/components/TextArea/RichTextArea.vue'

  export default defineComponent({
    name: 'CommunitySettings',
    components: { RichTextArea, Dropdown, ImageInput, DsTextarea, Loading, ToggleButton, ActionBar },
    mixins: [TranslationsMixin, CommunityMixin],
    props: {
      communityId: {
        type: [Number, String],
        required: true,
      },
    },
    data() {
      return {
        fetchingContent: false,
        communityData: null as any,
        originalCommunityData: null as any,
        saving: false,
        saved: false,
        errors: {} as any,
      }
    },
    computed: {
      areMessageBoardsEnabled(): boolean {
        return this.$store.getters.areMessageBoardsEnabled
      },
      areChallengesEnabled(): boolean {
        return this.$store.getters.areChallengesEnabled
      },
      isEditing(): boolean {
        return !isEqual(this.originalCommunityData, this.communityData)
      },
      hasFieldErrors(): boolean {
        if (!this.errors) return false
        return Object.keys(this.errors).some(k => k !== 'message' && this.errors[k])
      },
    },
    methods: {
      validate(): Record<string, string[]> {
        const errs: Record<string, string[]> = {}
        const name = (this.communityData.name || '').trim()
        const description = (this.communityData.description || '').trim()

        if (!name) {
          errs.name = [this.$t('community_settings_error_name_required')]
        } else if (name.length < 2) {
          errs.name = [this.$t('community_settings_error_name_min')]
        } else if (name.length > 40) {
          errs.name = [this.$t('community_settings_error_name_max')]
        }

        if (!description) {
          errs.description = [this.$t('community_settings_error_description_required')]
        } else if (description.length < 10) {
          errs.description = [this.$t('community_settings_error_description_min')]
        }

        return errs
      },
      async fetchCommunity() {
        this.fetchingContent = true
        this.errors = {}
        try {
          const communities = await ManageCommunities.get()
          const community = communities.find((c: any) => Number(c.id) === Number(this.communityId))
          if (!community) return

          if (community.image) {
            community.image = window.location.protocol + '//' + window.location.hostname + '/api/communities/' + community.id + '/image'
          }
          community.description = community.description.replace('<p>', '').replace('</p>', '')

          this.communityData = { ...community }
          this.originalCommunityData = { ...community }
        } catch (error) {
          console.log(error)
        } finally {
          this.fetchingContent = false
        }
      },
      saveCommunity() {
        this.saved = false
        this.errors = this.validate()
        if (this.hasFieldErrors) return

        this.saving = true

        ManageCommunities.post({ ...this.communityData })
          .then(() => {
            this.saved = true
            this.originalCommunityData = { ...this.communityData }
            this.$store.commit(COMMUNITY_MUTATION_TYPES.FLUSH_CACHE)
            this.$store.dispatch(COMMUNITY_ACTION_TYPES.FETCH_COMMUNITY_DETAIL, Number(this.communityId))
            setTimeout(() => {
              this.saved = false
            }, 3000)
          })
          .catch((errors: any) => {
            this.errors = errors
          })
          .finally(() => {
            this.saving = false
          })
      },
    },
    watch: {
      'communityData.color'(newColor: string) {
        if (!newColor) return
        document.documentElement.style.setProperty('--primary-community', newColor)
        document.documentElement.style.setProperty('--primary-community-dark', mix(0.9, newColor, '#000'))
        document.documentElement.style.setProperty('--primary-community-darker', mix(0.75, newColor, '#000'))
        document.documentElement.style.setProperty('--primary-community-darkest', mix(0.5, newColor, '#000'))
        document.documentElement.style.setProperty('--primary-community-extra-darkest', mix(0.25, newColor, '#000'))
        document.documentElement.style.setProperty('--primary-community-light', mix(0.9 * 100 / 255, newColor, '#fff'))
        document.documentElement.style.setProperty('--primary-community-lighter', mix(0.75 * 100 / 255, newColor, '#fff'))
        document.documentElement.style.setProperty('--primary-community-lightest', mix(0.5 * 100 / 255, newColor, '#fff'))
        document.documentElement.style.setProperty('--primary-community-extra-lightest', mix(0.25 * 100 / 255, newColor, '#fff'))
      },
    },
    mounted() {
      this.fetchCommunity()
    },
  })
</script>
<style scoped>
  :deep(.cs-textarea textarea) {
    height: 120px !important;
    width: 100%;
  }
</style>
